import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Filter, CreditCard, Clock, AlertTriangle, CheckCircle,
  XCircle, Pause, RotateCcw, Calendar, DollarSign, MoreVertical
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  trialing: { color: '#9F7AEA', bg: '#9F7AEA20', label: 'Trialing' },
  active: { color: '#38A169', bg: '#38A16920', label: 'Active' },
  past_due: { color: '#E53E3E', bg: '#E53E3E20', label: 'Past Due' },
  canceled: { color: '#718096', bg: '#71809620', label: 'Canceled' },
  paused: { color: '#D69E2E', bg: '#D69E2E20', label: 'Paused' },
  expired: { color: '#A0AEC0', bg: '#A0AEC020', label: 'Expired' }
};

const PLAN_PRICES = {
  starter: { monthly: 29, annual: 290 },
  professional: { monthly: 79, annual: 790 },
  premium: { monthly: 149, annual: 1490 },
  enterprise: { monthly: 299, annual: 2990 }
};

export default function SaaSSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ status: '', plan: '' });
  const [editingSub, setEditingSub] = useState(null);

  useEffect(() => { fetchSubscriptions(); }, [filters]);

  const fetchSubscriptions = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.plan) params.append('plan', filters.plan);
      const res = await axios.get(`${API}/saas/subscriptions?${params.toString()}`);
      setSubscriptions(res.data.items);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load subscriptions'); }
    finally { setLoading(false); }
  };

  const updateSubscription = async (id, data) => {
    try {
      await axios.put(`${API}/saas/subscriptions/${id}`, data);
      toast.success('Subscription updated');
      fetchSubscriptions();
      setEditingSub(null);
    } catch { toast.error('Failed to update subscription'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : 'N/A';

  return (
    <div className="saas-page" data-testid="saas-subscriptions">
      <div className="saas-page-header">
        <div>
          <h2>Subscriptions</h2>
          <p>{total} subscriptions</p>
        </div>
      </div>

      <div className="saas-filters">
        <div className="search-box">
          <Search size={18} />
          <input type="text" placeholder="Search subscriptions..." readOnly />
        </div>
        <select value={filters.status} onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))} className="filter-select" data-testid="filter-sub-status">
          <option value="">All Statuses</option>
          {Object.keys(STATUS_CONFIG).map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
        </select>
        <select value={filters.plan} onChange={(e) => setFilters(p => ({ ...p, plan: e.target.value }))} className="filter-select" data-testid="filter-sub-plan">
          <option value="">All Plans</option>
          <option value="starter">Starter</option>
          <option value="professional">Professional</option>
          <option value="premium">Premium</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>

      <div className="saas-table-container">
        <table className="saas-table" data-testid="subscriptions-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Provider</th>
              <th>Billing Cycle</th>
              <th>Amount</th>
              <th>Trial End</th>
              <th>Period End</th>
              <th>Payment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.map(sub => {
              const sc = STATUS_CONFIG[sub.status] || STATUS_CONFIG.active;
              return (
                <tr key={sub.id}>
                  <td>
                    <div className="tenant-cell">
                      <div className="tenant-avatar">{(sub.tenant_name || 'U').charAt(0)}</div>
                      <div className="tenant-info">
                        <strong>{sub.tenant_name || 'Unknown'}</strong>
                        <span>{sub.tenant_slug}</span>
                      </div>
                    </div>
                  </td>
                  <td><span className="plan-badge">{sub.plan_name}</span></td>
                  <td><span className="status-badge" style={{ background: sc.bg, color: sc.color }}>{sc.label}</span></td>
                  <td><span className={`module-badge ${sub.provider === 'stripe' ? 'stripe' : ''}`}>{sub.provider || 'manual'}</span></td>
                  <td>{sub.billing_cycle}</td>
                  <td className="amount-cell">${sub.amount?.toFixed(2) || '0.00'}/{sub.billing_cycle === 'annual' ? 'yr' : 'mo'}</td>
                  <td>{formatDate(sub.trial_end_at)}</td>
                  <td>{formatDate(sub.current_period_end)}</td>
                  <td><span className={`status-badge ${sub.payment_status}`}>{sub.payment_status}</span></td>
                  <td>
                    <div className="action-buttons">
                      {sub.status === 'trialing' && (
                        <button className="action-btn" title="Extend Trial" onClick={() => updateSubscription(sub.id, { trial_end_at: new Date(Date.now() + 14 * 86400000).toISOString() })} data-testid={`extend-trial-${sub.id}`}>
                          <Clock size={16} />
                        </button>
                      )}
                      {sub.status === 'trialing' && (
                        <button className="action-btn" title="Activate" onClick={() => updateSubscription(sub.id, { status: 'active', payment_status: 'paid' })} data-testid={`activate-${sub.id}`}>
                          <CheckCircle size={16} />
                        </button>
                      )}
                      {sub.status === 'active' && (
                        <button className="action-btn" title="Cancel" onClick={() => updateSubscription(sub.id, { status: 'canceled' })} data-testid={`cancel-${sub.id}`}>
                          <XCircle size={16} />
                        </button>
                      )}
                      {sub.status === 'canceled' && (
                        <button className="action-btn" title="Reactivate" onClick={() => updateSubscription(sub.id, { status: 'active' })} data-testid={`reactivate-${sub.id}`}>
                          <RotateCcw size={16} />
                        </button>
                      )}
                      {sub.status === 'active' && (
                        <button className="action-btn" title="Mark Past Due" onClick={() => updateSubscription(sub.id, { status: 'past_due', payment_status: 'failed' })} data-testid={`past-due-${sub.id}`}>
                          <AlertTriangle size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {subscriptions.length === 0 && !loading && (
          <div className="empty-state"><p>No subscriptions found</p></div>
        )}
      </div>
    </div>
  );
}
